import { captureException } from "@/posthog";
import { apiUser } from "@/components/toLoad/api";
import {
  IA_MODULE_ID,
  resolveQuota,
  estimateTextTokens,
  estimateImageTokens,
  monthlyAiTokens,
} from "@/components/toLoad/quotas";

const IACollection = IA_MODULE_ID;

function stripLeadingDiv(value) {
  if (value.slice(0, 5).toLowerCase() !== "<div>") return value;
  let start = 5;
  while (value[start] === " " || value[start] === "\t") start += 1;
  return value.slice(start);
}

function stripTrailingDiv(value) {
  let end = value.length;
  if (value[end - 1] === "\n") end -= 1;
  const close = "</div>";
  if (end < close.length || value.slice(end - close.length, end).toLowerCase() !== close) return value;
  let start = end - close.length;
  while (start > 0 && (value[start - 1] === " " || value[start - 1] === "\t")) start -= 1;
  return value.slice(0, start) + value.slice(end);
}

/**
 * Cleans HTML output to ensure Quill compatibility.
 * @param {string} html
 * @returns {string}
 */
function cleanQuillHtml(html) {
  if (!html || typeof html !== "string") return html;

  let cleaned = html.trim();

  cleaned = cleaned
    .replaceAll(/```html|```/gi, "")
    .replaceAll(/&nbsp;/gi, " ")
    .replaceAll("\r\n", "\n");

  cleaned = stripTrailingDiv(stripLeadingDiv(cleaned));

  cleaned = cleaned.replaceAll(/<li>\s*<p>([\s\S]*?)<\/p>\s*<\/li>/gi, "<li>$1</li>");

  cleaned = cleaned.replaceAll(/<li>([\s\S]*?)<\/li>/gi, (_match, content) => {
    let normalized = content;

    normalized = normalized
      .replaceAll(/^\s*<p>/gi, "")
      .replaceAll(/<\/p>\s*$/gi, "")
      .replaceAll(/<\/p>\s*<p>/gi, "<br>")
      .replaceAll(/<p>/gi, "")
      .replaceAll(/<\/p>/gi, "");

    normalized = normalized
      .replaceAll(/<h[1-6][^>]*>/gi, "")
      .replaceAll(/<\/h[1-6]>/gi, "")
      .replaceAll(/\n{2,}/g, "\n")
      .trim();

    return `<li>${normalized}</li>`;
  });

  cleaned = cleaned.replaceAll(/<(ol|ul)>\s*<(p|div)>/gi, "<$1>");
  cleaned = cleaned.replaceAll(/<\/(p|div)>\s*<\/(ol|ul)>/gi, "</$2>");
  cleaned = cleaned.replaceAll(/<p>\s*<\/p>/gi, "<p><br></p>");

  cleaned = cleaned
    .replaceAll(/>[ \t]+\n[ \t]*</g, "><")
    .replaceAll(/\n{3,}/g, "\n\n")
    .trim();

  return cleaned;
}

async function logCommand(userObject, command, tokens, response) {
  await apiUser({
    method: "post",
    url: `/companies/${userObject.company}/ia`,
    data: {
      command,
      tokens,
      author: userObject.id,
      response,
      creationDate: new Date(),
    },
  });
}

async function canRunAI(userObject, tokens) {
  const doc = await apiUser({
    method: "get",
    url: `/companies/${userObject.company}/modules/${IACollection}`,
  });
  if (!doc.data) throw new Error("[AI] This user can't run AI.");
  const quota = resolveQuota(doc.data.quota);
  if (quota === -1) return true;

  if (monthlyAiTokens(doc.data) + Number(tokens || 0) <= quota) return true;
  throw new Error("[AI] This user doesn't have enough tokens to run this AI prompt.");
}

export const ai = {
  imageSizes: Object.freeze({
    SQUARE: "1024x1024", // 1:1
    PORTRAIT: "768x1344", // 9:16
    LANDSCAPE: "1344x768", // 16:9
  }),

  imageRatios: Object.freeze({
    SQUARE: "1:1",
    PORTRAIT: "9:16",
    LANDSCAPE: "16:9",
  }),

  cleanQuillHtml,

  async writeMeAThing(
    userObject,
    prompt,
    {
      context = "",
      systemInstruction = "",
      type = "quill",
      length = -1,
    } = {},
  ) {
    const estimatedTokens = estimateTextTokens([prompt, context, systemInstruction], length);
    await canRunAI(userObject, estimatedTokens);

    let completion;
    try {
      const result = await apiUser({
        method: "post",
        url: "/ai/text",
        data: {
          prompt,
          context,
          systemInstruction,
          type,
          length,
        },
      });

      completion = result.data;
    } catch (error) {
      console.error("[AI] Unable to do chat completion", error);
      captureException(error, {
        flow: "ai_content_client",
      });
      throw error;
    }

    const tokensUsed = completion.usageMetadata ? completion.usageMetadata.totalTokens : 0;

    logCommand(userObject, prompt, tokensUsed, {
      type: "generateContent",
      text: completion.text,
    });

    return {
      text: completion.text,
      usageMetadata: completion.usageMetadata,
      candidates: completion.candidates ?? [],
    };
  },

  async generateImage(userObject, prompt, aspectRatio = this.imageRatios.SQUARE) {
    const tokensUsed = estimateImageTokens(prompt);

    await canRunAI(userObject, tokensUsed);

    try {
      const result = await apiUser({
        method: "post",
        url: "/ai/image",
        data: {
          prompt,
          aspectRatio,
          companyId: userObject.company,
        },
      });

      const imageUrl = result.data?.imageUrl;
      if (!imageUrl) throw new Error("[AI] No image URL returned.");

      logCommand(userObject, prompt, tokensUsed, {
        type: "generateImage",
        imageUrl,
      });

      return imageUrl;
    } catch (error) {
      console.error("[AI] Unable to generate image", error);
      captureException(error, {
        flow: "ai_image_client",
      });
      throw error;
    }
  },
};
